import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import {
    LayoutDashboard, Users, ShieldCheck, UsersRound, DollarSign, ClipboardList,
    CalendarDays, BarChart3, CheckSquare, CalendarCheck, ArrowLeftRight, Home,
    Clock, Bell, Settings, LogOut, ChevronsLeft, ChevronsRight, Menu, X
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import * as notificationService from '../../services/notificationService';
import BrandLogo from '../ui/BrandLogo';
import BottomNav from '../ui/BottomNav';

const roleName = (role: unknown) => {
    if (typeof role === 'string') return role;
    if (role && typeof role === 'object' && 'name' in role) return String((role as any).name);
    return '';
};

const hasAnyRole = (role: string, allowed: string[]) => {
    const normalized = role.toUpperCase();
    return allowed.some(item => normalized === item.toUpperCase() || normalized.includes(item.toUpperCase()));
};

const Layout = () => {
    const { user, logout, isAuthenticated } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const [isSidebarOpen, setIsSidebarOpen] = useState(true);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [unreadCount, setUnreadCount] = useState(0);
    const [notificationsOpen, setNotificationsOpen] = useState(false);
    const [notifications, setNotifications] = useState<notificationService.Notification[]>([]);
    const notificationButtonRef = useRef<HTMLButtonElement | null>(null);
    const notificationPanelRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        setIsDrawerOpen(false);
    }, [location.pathname]);

    useEffect(() => {
        if (!isAuthenticated) return;
        const fetchCount = async () => {
            try {
                const count = await notificationService.getUnreadCount();
                setUnreadCount(count);
            } catch {}
        };
        fetchCount();
        const interval = setInterval(fetchCount, 30000);
        return () => clearInterval(interval);
    }, [isAuthenticated]);

    useEffect(() => {
        if (!notificationsOpen) return;

        const handlePointerDown = (event: PointerEvent) => {
            const target = event.target as Node;
            if (notificationPanelRef.current?.contains(target)) return;
            if (notificationButtonRef.current?.contains(target)) return;
            setNotificationsOpen(false);
        };

        document.addEventListener('pointerdown', handlePointerDown, true);
        return () => document.removeEventListener('pointerdown', handlePointerDown, true);
    }, [notificationsOpen]);

    if (!isAuthenticated || !user) return null;

    const currentRole = roleName(user.role);
    const isAdmin = hasAnyRole(currentRole, ['Super Admin', 'ADMIN', 'Administrator', 'Station Manager']);
    const isController = hasAnyRole(currentRole, ['CONTROLLER']);
    const isOffice = hasAnyRole(currentRole, ['OFFICE']);
    const canApproveTimesheets = isAdmin || isController || isOffice;
    const showSelfServiceNav = !isAdmin;

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    const loadNotifications = async () => {
        try {
            const data = await notificationService.getNotifications(8);
            setNotifications(data.notifications);
            setUnreadCount(data.unreadCount);
        } catch {}
    };

    const toggleNotifications = async () => {
        const nextOpen = !notificationsOpen;
        setNotificationsOpen(nextOpen);
        if (nextOpen) await loadNotifications();
    };

    const openNotification = async (notification: notificationService.Notification) => {
        await notificationService.markAsRead(notification.id).catch(() => {});
        setUnreadCount(count => Math.max(0, count - (notification.isRead ? 0 : 1)));
        setNotificationsOpen(false);
        const route = notification.metadata?.route;
        if (route) navigate(route);
    };

    const markAllNotificationsRead = async () => {
        await notificationService.markAllAsRead();
        setUnreadCount(0);
        setNotifications(prev => prev.map(item => ({ ...item, isRead: true })));
    };

    const isActive = (path: string) => location.pathname === path || location.pathname.startsWith(`${path}/`);
    const pageTitle = location.pathname.split('/').filter(Boolean).pop()?.replace(/-/g, ' ') || 'Dashboard';
    const isRosterWorkspace = location.pathname.includes('/roster');

    const selfServiceNavItems = showSelfServiceNav ? [
        { to: '/dashboard', icon: Home, label: 'My Dashboard' },
        { to: '/timesheets/schedule', icon: CalendarDays, label: 'Schedule' },
        { to: '/my-roster', icon: ClipboardList, label: 'My Roster' },
        ...(!isController ? [{ to: '/team-roster', icon: Users, label: 'Team Roster' }] : []),
        { to: '/timesheets/my', icon: Clock, label: 'My Timesheets' },
        { to: '/leave/request', icon: CalendarDays, label: 'Leave Request' },
        { to: '/swaps', icon: ArrowLeftRight, label: 'Shift Swaps' },
    ] : [];

    const navItems = [
        ...(isAdmin ? [
            { to: '/admin/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
            { to: '/admin/users', icon: Users, label: 'Users' },
            { to: '/admin/skills', icon: ShieldCheck, label: 'Skills' },
            { to: '/admin/roles', icon: UsersRound, label: 'Roles' },
            { to: '/payroll', icon: DollarSign, label: 'Payroll' },
            { to: '/admin/roster', icon: ClipboardList, label: 'Roster' },
            { to: '/admin/patterns', icon: CalendarDays, label: 'Patterns' },
            { to: '/admin/approvals', icon: CalendarCheck, label: 'Request Approvals' },
        ] : []),
        ...(isController ? [
            { to: '/controller/dashboard', icon: BarChart3, label: 'Dashboard' },
            { to: '/controller/roster', icon: ClipboardList, label: 'Roster' },
            { to: '/payroll', icon: DollarSign, label: 'Payroll' },
        ] : []),
        ...(isOffice && !isAdmin && !isController ? [
            { to: '/payroll', icon: DollarSign, label: 'Payroll' },
        ] : []),
        ...(canApproveTimesheets ? [
            { to: '/timesheets/approvals', icon: CheckSquare, label: 'Approvals' },
        ] : []),
        ...selfServiceNavItems,
    ];

    const NavItem = ({ to, icon: Icon, label, mobile = false }: { to: string; icon: any; label: string; mobile?: boolean }) => (
        <Link
            to={to}
            title={!mobile && !isSidebarOpen ? label : undefined}
            aria-label={label}
            className={`flex items-center mb-1 rounded-xl transition-all duration-200 group ${mobile || isSidebarOpen ? 'px-4 py-3' : 'h-11 w-11 justify-center mx-auto'} ${isActive(to)
                ? mobile
                    ? 'bg-blue-50 text-blue-700'
                    : 'bg-blue-600 text-white shadow-lg shadow-blue-500/30'
                : mobile
                    ? 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                    : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                }`}
        >
            <Icon className={`w-5 h-5 shrink-0 ${mobile || isSidebarOpen ? 'mr-3' : ''}`} />
            <span className={`font-medium ${!mobile && !isSidebarOpen ? 'hidden' : ''}`}>{label}</span>
            {isActive(to) && !mobile && isSidebarOpen && <div className="ml-auto w-1.5 h-1.5 rounded-full bg-white shadow-sm" />}
        </Link>
    );

    const notificationOverlay = notificationsOpen ? createPortal(
        <div className="fixed inset-0 z-[1000]">
            <button
                className="absolute inset-0 cursor-default bg-white/45 backdrop-blur-[2px]"
                onClick={() => setNotificationsOpen(false)}
                aria-label="Close notifications"
            />
            <div
                ref={notificationPanelRef}
                className="absolute right-2 top-[4.25rem] z-[1001] w-[min(25rem,calc(100vw-1rem))] overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl shadow-slate-900/25 ring-1 ring-slate-900/5 sm:right-4 lg:right-6"
            >
                <div className="px-4 pb-2 pt-4">
                    <div className="flex items-start justify-between gap-4">
                    <div>
                        <p className="text-2xl font-extrabold tracking-tight text-slate-950">Notifications</p>
                        <p className="mt-0.5 text-xs font-medium text-slate-500">{unreadCount} unread</p>
                    </div>
                    <div className="flex items-center gap-2">
                        {unreadCount > 0 && (
                            <button onClick={markAllNotificationsRead} className="rounded-lg px-2 py-1 text-xs font-semibold text-blue-600 hover:bg-blue-50 hover:text-blue-700">
                                Mark all read
                            </button>
                        )}
                        <button
                            onClick={() => setNotificationsOpen(false)}
                            className="rounded-full bg-slate-100 p-2 text-slate-500 hover:bg-slate-200 hover:text-slate-800"
                            aria-label="Close notifications"
                        >
                            <X className="h-4 w-4" />
                        </button>
                    </div>
                    </div>
                    <div className="mt-3 flex gap-2">
                        <span className="rounded-full bg-blue-50 px-3 py-1.5 text-xs font-bold text-blue-700">All</span>
                        <span className="rounded-full px-3 py-1.5 text-xs font-bold text-slate-500">Unread</span>
                    </div>
                </div>
                <div className="max-h-[min(31rem,calc(100dvh-8rem))] overflow-y-auto px-2 pb-2">
                    {notifications.length === 0 ? (
                        <div className="rounded-xl px-4 py-8 text-center text-sm text-slate-400">No notifications</div>
                    ) : notifications.map(notification => (
                        <button
                            key={notification.id}
                            onClick={() => openNotification(notification)}
                            className={`group relative mb-1 flex w-full items-start gap-3 rounded-xl px-3 py-3 text-left transition-colors last:mb-0 ${notification.isRead ? 'hover:bg-slate-100' : 'bg-blue-50 hover:bg-blue-100/80'}`}
                        >
                            <div className="relative flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-slate-200 text-slate-700">
                                <Bell className="h-5 w-5" />
                                <span className="absolute -bottom-0.5 -right-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-blue-600 text-white ring-2 ring-white">
                                    <Bell className="h-3 w-3" />
                                </span>
                            </div>
                            <div className="min-w-0 flex-1 pr-5">
                                <p className="text-sm leading-5 text-slate-800">
                                    <span className="font-bold text-slate-950">{notification.title}</span>
                                    <span className="ml-1">{notification.message}</span>
                                </p>
                                <p className={`mt-1 text-xs font-semibold ${notification.isRead ? 'text-slate-400' : 'text-blue-600'}`}>
                                    {new Date(notification.createdAt).toLocaleString()}
                                </p>
                            </div>
                            {!notification.isRead && (
                                <span className="absolute right-3 top-1/2 h-2.5 w-2.5 -translate-y-1/2 rounded-full bg-blue-600" />
                            )}
                        </button>
                    ))}
                </div>
            </div>
        </div>,
        document.body
    ) : null;

    return (
        <div className="min-h-screen bg-slate-50">
            {notificationOverlay}
            <aside className={`${isSidebarOpen ? 'w-60' : 'w-16'} hidden lg:flex bg-slate-900 text-white transition-all duration-300 flex-col fixed h-full z-30 shadow-2xl`}>
                <div className={`${isSidebarOpen ? 'p-5' : 'px-2 py-5'} flex items-center justify-between`}>
                    <div className={`flex items-center gap-3 ${!isSidebarOpen && 'justify-center w-full'}`}>
                        <BrandLogo size={isSidebarOpen ? 'md' : 'sm'} containerClassName={isSidebarOpen ? '' : 'px-1.5 py-1'} />
                    </div>
                </div>

                <div className={`${isSidebarOpen ? 'px-3' : 'px-1.5'} flex-1 py-4 overflow-y-auto`}>
                    <p className={`px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider mb-4 ${!isSidebarOpen && 'sr-only'}`}>
                        {isSidebarOpen ? 'Menu' : '...'}
                    </p>
                    {navItems.map(item => <NavItem key={item.to} {...item} />)}
                </div>

                <div className={`${isSidebarOpen ? 'p-3' : 'p-2'} border-t border-slate-800`}>
                    <button
                        onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                        className="w-full flex items-center justify-center h-10 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
                        aria-label="Toggle sidebar"
                    >
                        {isSidebarOpen ? <ChevronsLeft className="w-5 h-5" /> : <ChevronsRight className="w-5 h-5" />}
                    </button>
                </div>
            </aside>

            {isDrawerOpen && (
                <div className="fixed inset-0 z-50 lg:hidden">
                    <button className="absolute inset-0 bg-slate-950/50" onClick={() => setIsDrawerOpen(false)} aria-label="Close menu" />
                    <div className="relative h-full w-[84vw] max-w-sm bg-white shadow-xl flex flex-col">
                        <div className="h-16 px-4 border-b border-slate-200 flex items-center justify-between">
                            <BrandLogo size="sm" />
                            <button onClick={() => setIsDrawerOpen(false)} className="p-2 rounded-lg text-slate-500 hover:bg-slate-100" aria-label="Close menu">
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <div className="flex-1 overflow-y-auto p-4">
                            {navItems.map(item => <NavItem key={item.to} {...item} mobile />)}
                        </div>
                        <div className="p-4 border-t border-slate-200">
                            <button onClick={handleLogout} className="w-full flex items-center px-4 py-3 rounded-xl text-red-600 hover:bg-red-50">
                                <LogOut className="w-5 h-5 mr-3" />
                                Logout
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <main className={`min-h-screen transition-all duration-300 ${isSidebarOpen ? 'lg:ml-60' : 'lg:ml-16'}`}>
                <header className="h-16 bg-white/90 backdrop-blur-md border-b border-slate-200 sticky top-0 z-20 px-3 sm:px-6 lg:px-8 flex items-center justify-between">
                    <div className="flex items-center gap-3 min-w-0">
                        <button onClick={() => setIsDrawerOpen(true)} className="lg:hidden p-2 -ml-1 rounded-lg text-slate-600 hover:bg-slate-100" aria-label="Open menu">
                            <Menu className="w-5 h-5" />
                        </button>
                        <h2 className="text-base sm:text-xl font-semibold text-slate-800 capitalize truncate">{pageTitle}</h2>
                    </div>

                    <div className="flex items-center gap-2 sm:gap-4">
                        <div className="relative">
                            <button ref={notificationButtonRef} onClick={toggleNotifications} className="relative p-2 text-slate-400 hover:text-slate-700 transition-colors" title="Notifications" aria-label="Notifications">
                                <Bell className="w-5 h-5" />
                                {unreadCount > 0 && (
                                    <span className="absolute -top-0.5 -right-0.5 px-1.5 py-0.5 text-[10px] font-bold bg-red-500 text-white rounded-full min-w-[18px] text-center leading-none">
                                        {unreadCount > 9 ? '9+' : unreadCount}
                                    </span>
                                )}
                            </button>
                        </div>
                        <Link to="/settings" className="hidden sm:block p-2 text-slate-400 hover:text-slate-700 transition-colors" title="Settings">
                            <Settings className="w-5 h-5" />
                        </Link>
                        <div className="flex items-center gap-2 sm:gap-3 sm:pl-4 sm:border-l border-slate-200">
                            <div className="text-right hidden sm:block">
                                <p className="text-sm font-medium text-slate-900">{user.name}</p>
                                <p className="text-xs text-slate-500 capitalize">{currentRole.toLowerCase()}</p>
                            </div>
                            <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-slate-200 flex items-center justify-center text-slate-600 font-bold border-2 border-white shadow-sm">
                                {user.name.charAt(0)}
                            </div>
                            <button onClick={handleLogout} className="hidden sm:block p-2 text-slate-400 hover:text-red-600 transition-colors" title="Logout">
                                <LogOut className="w-5 h-5" />
                            </button>
                        </div>
                    </div>
                </header>

                <div
                    key={location.pathname}
                    className={`${isRosterWorkspace ? 'p-2 sm:p-3 lg:p-4' : 'p-3 sm:p-6 lg:p-8'} pb-24 lg:pb-8`}
                    style={{ animation: 'pageFadeIn 0.2s ease-out' }}
                >
                    <Outlet />
                </div>
            </main>
            <BottomNav />
        </div>
    );
};

export default Layout;
